import { useEffect, useRef, useState } from 'react'
import { useChat } from '@ai-sdk/react'
import { generateId } from 'ai'
import { useNinetailed } from '@ninetailed/experience.js-react'
import { datadogRum } from '@datadog/browser-rum'

import { createAlgoliaChatTransport, sendFeedback } from './api'
import { sanitizeForLLM } from './utils/sanitize'
import { useTranslations } from '@/utils/locale'
import { ChatMessage } from './types'
import routes from '@/constants/routes'

/**
 * Shared Algolia Agent Studio chat plumbing (transport, input sanitization,
 * feedback) used by both the floating chat (`AiSearchField`) and any other
 * self-contained Ask AI surface (e.g. a hero-embedded widget) so the safety
 * checks and error handling live in one place.
 */
export function useAskAiChat(agentId?: string, trackingMetadata?: Record<string, any>) {
  const { t } = useTranslations()
  const { track } = useNinetailed()
  const [transportError, setTransportError] = useState<string | null>(null)
  const [chatId, setChatId] = useState(generateId)

  const { messages, status, sendMessage, stop, error, setMessages, clearError } = useChat<ChatMessage>({
    id: chatId,
    transport: createAlgoliaChatTransport(agentId),
    onError: (chatError) => {
      try {
        const parsedError = JSON.parse(chatError.message)
        setTransportError(parsedError.message)
      } catch {
        setTransportError(chatError.message || t('An unexpected error occurred. Please try again later.'))
      }
      datadogRum.addError(chatError, {
        source: 'ask-ai-chat',
      })
    },
  })

  const isLoading = status === 'submitted' || status === 'streaming'

  // Dev-only timing: logs time-to-first-token and total round trip per message
  const timingRef = useRef<{ start: number; firstTokenAt: number | null } | null>(null)
  useEffect(() => {
    if (!import.meta.env.DEV) return
    const timing = timingRef.current
    if (!timing) return

    if (status === 'streaming' && timing.firstTokenAt === null) {
      timing.firstTokenAt = performance.now()
    }

    if (status === 'ready' || status === 'error') {
      const totalMs = performance.now() - timing.start
      const firstTokenMs = timing.firstTokenAt !== null ? timing.firstTokenAt - timing.start : null
      console.debug(
        `[Ask AI] message ${status === 'error' ? 'failed' : 'completed'} in ${totalMs.toFixed(0)}ms` +
          (firstTokenMs !== null ? ` (first token: ${firstTokenMs.toFixed(0)}ms)` : '')
      )
      timingRef.current = null
    }
  }, [status])

  const sendPrompt = async (message: string) => {
    setTransportError(null)
    const { isSafe } = sanitizeForLLM(message)
    if (!isSafe) {
      setTransportError(t('Your input may contain harmful or malicious content.'))
      return
    }

    if (import.meta.env.DEV) {
      timingRef.current = { start: performance.now(), firstTokenAt: null }
    }

    try {
      await sendMessage({
        parts: [{ type: 'text', text: message }],
        metadata: {
          turnContext: {
            url: `${routes.hostname}${window.location.pathname}`,
            locale: navigator.language,
          },
          ...(trackingMetadata || {}),
        },
      })
    } catch (sendError: any) {
      setTransportError(sendError?.message || t('An unexpected error occurred. Please try again later.'))
      console.error('Error sending message:', sendError)
      datadogRum.addError(sendError, {
        source: 'ask-ai-chat',
      })
    }
    track('ask-ai-prompt-sent', trackingMetadata)
  }

  const handleFeedback = (messageId: string, feedback: 'up' | 'down') => {
    sendFeedback(messageId, feedback, agentId)
  }

  const reset = () => {
    stop()
    setMessages([])
    clearError()
    setTransportError(null)
    setChatId(generateId())
  }

  return { messages, status, isLoading, error, transportError, sendPrompt, stop, handleFeedback, reset }
}
